"use client";

import { motion } from "framer-motion";
import {
  TriangleAlert,
  Play,
  CircleCheckBig,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import { getViewModeChrome } from "../view-mode-chrome";

interface ErrorLoggingDisplayProps {
  result: {
    status?: string;
    error_type?: string;
    error_message?: string;
    stage?: string;
    timestamp?: number;
    id?: string;
  };
  onContinue?: () => void;
  isLastMessage?: boolean;
  isCompleted?: boolean;
  viewMode?: string;
}

/**
 * Error / pause / retry states — single-line, tinted by session viewMode.
 */
export function ErrorLoggingDisplay({
  result,
  onContinue,
  isLastMessage = false,
  isCompleted = false,
  viewMode,
}: ErrorLoggingDisplayProps) {
  const chrome = getViewModeChrome(viewMode);
  const FOCUS_RING = `focus-visible:outline-none focus-visible:ring-2 ${chrome.focusRing} focus-visible:ring-offset-2 focus-visible:ring-offset-background`;

  const errorMessage = result?.error_message;
  const stage = result?.stage;

  const isUserCanceled =
    errorMessage === "User requested stop" && stage === "user_cancel";
  const isMaxIterations =
    errorMessage === "Max agent turns reached" &&
    stage === "max_iterations_guard";
  const isToolTimeout =
    result?.error_type === "tool_timeout" || stage === "tool_timeout";

  let label: string;
  let Icon: LucideIcon;
  let spin = false;

  if (isUserCanceled) {
    label = "Stopped";
    Icon = TriangleAlert;
  } else if (isMaxIterations) {
    if (isCompleted) {
      label = "Resuming";
      Icon = CircleCheckBig;
    } else {
      label = "Paused";
      Icon = Play;
    }
  } else if (isToolTimeout) {
    label = "Retrying";
    Icon = Loader2;
    spin = true;
  } else {
    label = "Refining approach";
    Icon = Loader2;
    spin = true;
  }

  const showContinue =
    isMaxIterations && !!onContinue && isLastMessage && !isCompleted;

  return (
    <motion.div
      initial={{ opacity: 0, y: 4, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      className="w-full"
    >
      <div className={`rounded-md border ${chrome.card} overflow-hidden`}>
        <div className="px-3 py-2 flex items-center gap-2.5 min-w-0">
          <Icon
            className={`w-3.5 h-3.5 ${chrome.iconMuted} shrink-0 ${spin ? "animate-spin" : ""}`}
            strokeWidth={2}
          />
          <span className={`text-[13px] font-medium ${chrome.text} truncate`}>
            {label}
          </span>
        </div>

        {showContinue && (
          <div className={`border-t ${chrome.divider} px-3 py-2`}>
            <button
              type="button"
              onClick={onContinue}
              className={`w-full h-8 rounded-md ${chrome.button} text-sm font-medium inline-flex items-center justify-center gap-1.5 transition-colors ${FOCUS_RING}`}
            >
              <Play className="w-4 h-4" strokeWidth={2.4} />
              Continue
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}
